import React, { useState } from 'react';
import './styles/design-system.css';
import './styles/topic-selector.css';
import './styles/session-interface.css';
import './styles/voice-interview.css';
import './styles/report.css';
import './styles/chat-interface.css';
import './styles/nano.css';
import './styles/auth.css';
import './styles/immersive.css';   // the immersive skin — must import LAST (cascade wins)
import LandingPage from './components/LandingPage';
import TopicSelector from './components/TopicSelector';
import SessionInterface from './components/SessionInterface';
import VoiceInterviewInterface from './components/VoiceInterviewInterface';
import ReportDashboard from './components/ReportDashboard';
import ChatInterface from './components/ChatInterface';
import NanoApp from './components/nano/NanoApp';
import CreatorApp from './components/creator/CreatorApp';
import AdminApp from './components/admin/AdminApp';
import APIService from './services/APIService';
import { AuthProvider, useAuth } from './context/AuthContext';
import LoginPage from './components/auth/LoginPage';
import ComingSoon from './components/ComingSoon';
import { COMING_SOON } from './config/comingSoon';

function AppRoutes({ onEnterCreator, onEnterAdmin }) {
  const [currentPage, setCurrentPage] = useState('nano');
  const [contentLoaded, setContentLoaded] = useState(false);
  const [currentSession, setCurrentSession] = useState(null);
  const [completedReport, setCompletedReport] = useState(null);

  const handleContentLoaded = () => {
    setContentLoaded(true);
  };

  const handleNavigateTo = (page) => {
    setCurrentPage(page);
  };

  const handleSessionStart = (sessionData) => {
    if (sessionData.chat_mode) {
      setCurrentPage('chat');
      return;
    }
    setCurrentSession(sessionData);
    if (sessionData.voice_interview) {
      setCurrentPage('voice-interview');
    } else {
      setCurrentPage('session');
    }
  };

  const handleSessionComplete = (report) => {
    setCompletedReport(report);
    setCurrentSession(null);
    setCurrentPage('report');
  };

  const handleStartNewSession = () => {
    setCurrentSession(null);
    setCompletedReport(null);
    setCurrentPage('nano');
  };

  const handleExit = () => {
    setCurrentSession(null);
    setCompletedReport(null);
    setContentLoaded(false);
    setCurrentPage('nano');
  };

  const handleCancelSession = async () => {
    // try to delete the session on the server
    if (currentSession && currentSession.session_id) {
      try {
        await APIService.deleteSession(currentSession.session_id);
      } catch (err) {
        console.warn('Could not delete session on server:', err);
      }
    }
    setCurrentSession(null);
    setCurrentPage('nano');
  };

  // Launch the WebRTC voice interview from DataVerse.ai's AI Interviewer mode.
  // The graph/session topics may not exist in the backend content, so we load
  // an available subject and use ITS real topics (falling back to all).
  const handleLaunchVoiceInterview = async (config) => {
    try {
      const { subjects = [] } = await APIService.getSubjects();
      if (!subjects.length) throw new Error('No study content is available on the server.');
      await APIService.loadSubject(subjects[0]);

      const { topics = [] } = await APIService.getTopics();
      if (!topics.length) throw new Error('The loaded subject has no topics.');

      // Prefer the user's selected topics if they exist in the content; else use all.
      const wanted = (config?.subjects || []).map((s) => String(s.name).toLowerCase());
      const matched = topics.filter((t) => wanted.includes(String(t).toLowerCase()));
      const useTopics = matched.length ? matched : topics;

      const session = await APIService.startSession(useTopics, null, true, { voice_interview: true });
      if (!session || !session.session_id) throw new Error(session?.error || 'Could not create a session.');

      setCurrentSession({ ...session, voice_interview: true });
      setCurrentPage('voice-interview');
    } catch (err) {
      console.error('Could not start voice interview:', err);
      alert(`Could not start the voice interview: ${err.message}`);
    }
  };

  return (
    <div className="App">
      {currentPage === 'nano' && (
        <NanoApp onLaunchVoiceInterview={handleLaunchVoiceInterview} onEnterCreator={onEnterCreator} onEnterAdmin={onEnterAdmin} />
      )}

      {currentPage === 'landing' && (
        <LandingPage onContentLoaded={handleContentLoaded} onNavigateTo={handleNavigateTo} />
      )}

      {currentPage === 'topic-selector' && contentLoaded && (
        <TopicSelector onSessionStart={handleSessionStart} onBack={() => setCurrentPage('landing')} />
      )}

      {currentPage === 'session' && currentSession && (
        <SessionInterface
          sessionData={currentSession}
          onSessionComplete={handleSessionComplete}
          onCancel={handleCancelSession}
        />
      )}

      {currentPage === 'voice-interview' && currentSession && (
        <VoiceInterviewInterface
          sessionData={currentSession}
          onComplete={handleSessionComplete}
          onCancel={handleCancelSession}
        />
      )}

      {currentPage === 'report' && completedReport && (
        <ReportDashboard report={completedReport} onStartNewSession={handleStartNewSession} onExit={handleExit} />
      )}

      {currentPage === 'chat' && (
        <ChatInterface onBack={() => setCurrentPage('topic-selector')} />
      )}
    </div>
  );
}

// Gate: landing → login → app. The public landing is the front door; the
// login form only appears after a CTA click (never as the first impression).
// Which surface this BUILD serves. 'all' (default) = today's single app with
// in-app switching; 'user' | 'creator' | 'admin' produce standalone bundles for
// separate hosting (npm run build:user / build:creator / build:admin).
const SURFACE = process.env.REACT_APP_SURFACE || 'all';
// Where the learner app lives when this build is a standalone creator/admin
// surface — "Back to learner mode" navigates there instead of switching in-app.
const USER_APP_URL = process.env.REACT_APP_USER_URL || '';

function AuthGate() {
  // Pre-launch lock (src/config/comingSoon.js): only the PrepareUp.ai
  // coming-soon page is shown — no login screen, no app, even for a browser
  // that still holds a signed-in session.
  if (!COMING_SOON.loginEnabled) return <ComingSoon />;
  return <OpenAuthGate />;
}

function OpenAuthGate() {
  const { user, loading } = useAuth();
  const [wantsLogin, setWantsLogin] = useState(false);
  const [surface, setSurface] = useState(SURFACE === 'all' ? 'user' : SURFACE);
  const exitToUser = () => {
    if (SURFACE === 'all') setSurface('user');
    else if (USER_APP_URL) window.location.href = USER_APP_URL;
  };

  if (loading) {
    return (
      <div className="nano-root">
        <div className="nano-glow" aria-hidden="true" />
        <div className="nano-auth-loading">
          <div className="nano-auth-spinner" />
          <span>Loading DataVerse.ai…</span>
        </div>
      </div>
    );
  }

  if (user) {
    if (surface === 'creator') return <CreatorApp onExit={exitToUser} userName={user.displayName || ''} />;
    if (surface === 'admin') return <AdminApp onExit={exitToUser} />;
    return (
      <AppRoutes
        onEnterCreator={SURFACE === 'all' ? () => setSurface('creator') : undefined}
        onEnterAdmin={SURFACE === 'all' ? () => setSurface('admin') : undefined}
      />
    );
  }
  // Signed-out visitors see the PrepareUp.ai page; the login opens at #signin.
  if (wantsLogin) return <LoginPage />;
  return <ComingSoon onSignIn={() => setWantsLogin(true)} />;
}

function App() {
  return (
    <AuthProvider>
      <AuthGate />
    </AuthProvider>
  );
}

export default App;